import React from 'react';
import LegalLayout from '../components/LegalLayout';

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="February 26, 2026">
      <p>
        This Privacy Policy explains how <strong>Facade Lighting Studio</strong> ("we", "our", "us") collects, uses, and
        protects information when you use our website and services. By using the service, you agree to this policy.
      </p>

      <h2>1. Information we collect</h2>
      <ul>
        <li>
          <strong>Account information</strong> — when you register, we collect your email address, name (optional), and a
          password hash. If you sign in with Google, we receive your email, name, and profile picture.
        </li>
        <li>
          <strong>Project content</strong> — building photos you upload and lighting designs you create. Photos are stored
          locally in your browser by default. Saved projects (when you choose to save) are stored on our servers.
        </li>
        <li>
          <strong>Payment information</strong> — handled entirely by our payment processors (Stripe and, where applicable,
          Razorpay). We never see or store your full card number, CVV, or UPI PIN. We only receive a transaction reference
          and the amount paid.
        </li>
        <li>
          <strong>Usage data</strong> — log entries of API calls (e.g., AI Enhance requests), timestamps, and basic device
          metadata (IP address, browser user-agent) used for security and abuse prevention.
        </li>
        <li>
          <strong>Feedback</strong> — ratings and comments you submit via the in-app feedback widget.
        </li>
      </ul>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To create and manage your account.</li>
        <li>To process payments and grant access to paid features.</li>
        <li>To process AI-powered image enhancements (your image is sent to Google Gemini for enhancement, then returned).</li>
        <li>To prevent fraud, abuse, and brute-force login attacks.</li>
        <li>To respond to support requests and improve the product.</li>
      </ul>

      <h2>3. Third parties we share data with</h2>
      <ul>
        <li><strong>Stripe</strong> — payment processing for international customers.</li>
        <li><strong>Razorpay</strong> — payment processing for Indian customers (when enabled).</li>
        <li><strong>Google Gemini</strong> — AI image enhancement. Images you submit for AI Finalize are sent to Google's API.</li>
        <li><strong>Google OAuth</strong> — only if you choose to sign in with Google.</li>
        <li><strong>Hosting and infrastructure providers</strong> required to operate the service.</li>
      </ul>
      <p>We do not sell your data to advertisers or data brokers.</p>

      <h2>4. Data retention</h2>
      <p>
        We retain account data for as long as you have an active account. You may request deletion at any time by emailing
        us at <a href="mailto:xpertzankur@gmail.com">xpertzankur@gmail.com</a>. Payment transaction records are retained for
        the period required by applicable tax and accounting laws (typically 7 years in India).
      </p>

      <h2>5. Cookies and local storage</h2>
      <p>
        We use cookies and your browser's localStorage to keep you signed in, auto-save work in progress, and remember UI
        preferences (sidebar width, collapsed sections). We do not use third-party advertising cookies.
      </p>

      <h2>6. Security</h2>
      <p>
        Passwords are hashed using bcrypt. Authentication tokens are signed JWTs with limited expiry. Communications are
        encrypted in transit (HTTPS). We follow industry-standard security practices but no system is 100% secure — please
        report any suspected vulnerability to <a href="mailto:xpertzankur@gmail.com">xpertzankur@gmail.com</a>.
      </p>

      <h2>7. Your rights</h2>
      <p>
        You can access, correct, export, or delete your personal data at any time by emailing us. If you are in the
        European Union or other jurisdictions with data-protection regulations (GDPR, etc.), you have the additional rights
        to object to processing and to data portability under those laws.
      </p>

      <h2>8. Children</h2>
      <p>The service is not directed at children under 13. We do not knowingly collect personal data from children.</p>

      <h2>9. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes will be communicated via email or an in-app notice
        at least 7 days before they take effect.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions about privacy? Email <a href="mailto:xpertzankur@gmail.com">xpertzankur@gmail.com</a>.
      </p>
    </LegalLayout>
  );
}
